import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Coffee,
  Hourglass,
  Lock,
  LogIn,
  LogOut,
  Moon,
  MonitorCheck,
  MonitorOff,
  MonitorSmartphone,
  Pause,
  Play,
  ShieldCheck,
  ShieldX,
  Sun,
  Undo2,
  Unlock,
  UserCheck,
  UserX,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDays, formatMinutes, formatShortTime } from '../lib/format';
import {
  isFreeShift,
  presenceLabel,
  type AttendanceSession,
  type DaySummary,
  type FreeShifts,
  type PresenceReason,
  type PresenceView,
} from '../components/ShiftControl';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  Table,
  Td,
  Th,
  TimeRange,
  Tr,
  statusTone,
} from '../components/ui';

interface TimelineItem {
  at: string;
  kind: string;
  metadata?: Record<string, any> | null;
}

interface IdleStretch {
  id: string;
  reason: PresenceReason;
  lastActivityAt: string;
  startedAt: string;
  endedAt: string | null;
  /** WORK_PAUSED: they paused work (2026-10-24) — like a break, a pause is never idle time. */
  endReason: 'ACTIVITY_RESUMED' | 'SHIFT_ENDED' | 'BREAK_STARTED' | 'AGENT_RECONNECTED' | 'WORK_PAUSED' | null;
  penaltyDueAt: string | null;
  /** HOURS_MET: begun once the day's hours were worked, so no penalty (2026-10-21). FREE_SHIFT: in a Free Shift, never one (CR-006). */
  penaltyOutcome: 'APPLIED' | 'CAP_REACHED' | 'NOT_REACHED' | 'DISABLED' | 'HOURS_MET' | 'FREE_SHIFT' | null;
  penalty: { id: string; days: string | number | null; status: string } | null;
}

interface ShiftDetail extends AttendanceSession {
  user: { id: string; fullName: string; fullNameAr: string | null };
  summary: DaySummary;
  presence: PresenceView | null;
  idleSessions: IdleStretch[];
  timeline: TimelineItem[];
  /** `setEnd`: a forgotten shift no agent tracked, waiting for a manager to set its real end. */
  can: { end: boolean; setEnd?: boolean };
  /** A Free Shift's: the Regular Shift it follows (CR-006), and its place after it. */
  anchor?: { id: string; workDate: string; startedAt: string; endedAt: string | null } | null;
  number?: number | null;
  /** A Regular Shift's: the Free Shifts after it — tracked, never in its worked time. */
  free?: FreeShifts | null;
}

/**
 * Route: `/attendance/sessions/:id` — one shift and its timeline (the agent CR's
 * §46.3, §47): the start, breaks, Working ⇄ Not working, the agent coming and
 * going, penalties, the end — and each stretch of Not working with what it came
 * to. The person who worked it, and their manager.
 */
export function AttendanceShiftPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const shift = useQuery({
    queryKey: ['attendance', 'session', id],
    queryFn: () => api.get<ShiftDetail>(`/attendance/sessions/${id}`),
    refetchInterval: (q) => (q.state.data && !q.state.data.endedAt ? 20_000 : false),
  });

  const s = shift.data;
  const own = s?.user.id === user?.id;
  // Opened from an employee's month of shifts (2026-10-27): back to that month.
  const month = (location.state as { back?: string } | null)?.back;
  const back = month ?? (own || !can(P.ATTENDANCE_MANAGE) ? '/attendance' : '/attendance?tab=team');

  if (shift.isLoading) return <LoadingBlock />;
  if (shift.error || !s) return <ErrorState error={shift.error} onRetry={() => void shift.refetch()} />;

  const penaltyDays = s.idleSessions
    .filter((i) => i.penalty && i.penalty.status !== 'CANCELLED')
    .reduce((sum, i) => sum + Number(i.penalty!.days ?? 0), 0);

  return (
    <>
      <div className="mb-4">
        <Link
          to={back}
          onClick={(e) => {
            // Back through the history, so the month keeps its own way back to the board it came from.
            if (month && location.key !== 'default') {
              e.preventDefault();
              navigate(-1);
            }
          }}
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {month ? localized(s.user) : t('attendance.title')}
        </Link>
      </div>
      <PageHeader
        title={
          isFreeShift(s)
            ? `${t('attendance.freeShiftNumber', { n: s.number ?? 1 })} · ${own ? formatDate(s.workDate, locale) : `${localized(s.user)} · ${formatDate(s.workDate, locale)}`}`
            : own
              ? t('attendance.shiftOn', { date: formatDate(s.workDate, locale) })
              : t('attendance.shiftOf', { name: localized(s.user), date: formatDate(s.workDate, locale) })
        }
        subtitle={
          s.agentDevice ? t('attendance.trackedBy', { name: s.agentDevice.name }) : t('attendance.notTracked')
        }
        action={
          s.can.end ? (
            <Button variant="danger" icon={<LogOut className="h-4 w-4 flip-rtl" />} onClick={() => navigate(`/attendance/sessions/${s.id}/end`)}>
              {isFreeShift(s) ? t('attendance.endFree') : t('attendance.endShift')}
            </Button>
          ) : s.can.setEnd ? (
            <Button variant="primary" icon={<Clock className="h-4 w-4" />} onClick={() => navigate(`/attendance/sessions/${s.id}/end`)}>
              {t('attendance.setEnd')}
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-5">
        {/* A Free Shift (CR-006): after the day's Regular Shift, tracked, unpaid. */}
        {isFreeShift(s) && (
          <Card>
            <p className="text-body-sm text-[var(--text-primary)]">
              {s.anchor ? (
                <Link to={`/attendance/sessions/${s.anchor.id}`} className="text-primary-600 hover:underline dark:text-primary-400">
                  {t('attendance.freeAfter', { date: formatDate(s.anchor.workDate, locale) })}
                </Link>
              ) : (
                t('attendance.freeShift')
              )}
            </p>
            <p className="mt-1 text-body-sm text-muted">{t('attendance.freeUnpaid')}</p>
          </Card>
        )}
        {s.endUnconfirmed && (
          <Card className="border-warning/40">
            <p className="flex items-center gap-2 text-body font-medium text-warning-fg dark:text-warning-dark">
              <Hourglass className="h-4 w-4" aria-hidden />
              {t('attendance.endUnconfirmed')}
            </p>
            <p className="mt-1 text-body-sm text-muted">{t('attendance.setEndNote')}</p>
          </Card>
        )}
        {s.presence?.tracked && s.presence.status === 'NOT_WORKING' && (
          <Card className="border-error/40">
            <p className="flex items-center gap-2 text-body font-medium text-error-fg dark:text-error-dark">
              <UserX className="h-4 w-4" aria-hidden />
              {presenceLabel(s.presence, t)} · {t('attendance.since', { time: formatShortTime(s.presence.since, locale) })}
            </p>
            {s.presence.penaltyDueAt && (
              <p className="mt-1 text-body-sm text-error-fg dark:text-error-dark">
                {t('presence.penaltyAtOther', { time: formatShortTime(s.presence.penaltyDueAt, locale) })}
              </p>
            )}
          </Card>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label={t('attendance.worked')}
            value={formatMinutes(s.summary.workedMinutes, locale)}
            // Paused time came off it, and is no break (2026-10-24).
            hint={s.summary.pausedMinutes ? t('attendance.pausedFor', { minutes: formatMinutes(s.summary.pausedMinutes, locale) }) : undefined}
            icon={<Clock className="h-4 w-4" />}
          />
          <Metric
            label={t('attendance.breaks')}
            value={formatMinutes(s.summary.breakMinutes, locale)}
            // A meeting's time stayed in Worked: the breaks say how much of theirs did.
            hint={
              s.summary.workBreakMinutes
                ? t('attendance.workBreaks', { minutes: formatMinutes(s.summary.workBreakMinutes, locale) })
                : undefined
            }
            icon={<Coffee className="h-4 w-4" />}
          />
          <Metric
            label={t('attendance.notWorking')}
            value={formatMinutes(s.summary.notWorkingMinutes, locale)}
            icon={<UserX className="h-4 w-4" />}
            tone={s.summary.notWorkingMinutes > 0 ? 'error' : 'neutral'}
          />
          <Metric
            label={t('attendance.idlePenalties')}
            value={penaltyDays > 0 ? formatDays(penaltyDays, locale) : '—'}
            hint={penaltyDays > 0 ? t('attendance.daysOfSalary') : undefined}
            tone={penaltyDays > 0 ? 'error' : 'neutral'}
          />
        </div>

        {/* A Regular Shift's Free Shifts (CR-006): listed apart — none of their time is in this shift's. */}
        {s.free && s.free.count > 0 && (
          <Card>
            <CardHeader title={t('attendance.freeShiftsAfter')} subtitle={t('attendance.freeShiftsAfterHint')} />
            <ul className="space-y-1 text-body-sm">
              {s.free.sessions.map((f) => (
                <li key={f.id} className="flex flex-wrap items-baseline gap-x-2 tabular">
                  <Link to={`/attendance/sessions/${f.id}`} className="text-primary-600 hover:underline dark:text-primary-400">
                    {t('attendance.freeShiftNumber', { n: f.number })}
                  </Link>
                  <span className="text-muted">
                    <TimeRange from={f.startedAt} to={f.endedAt} />
                  </span>
                  <span>{formatMinutes(f.summary.workedMinutes, locale)}</span>
                  {!f.endedAt && <span className="text-caption text-faint">{t('attendance.freeSoFar')}</span>}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div className="grid gap-5 lg:grid-cols-5">
          <Card className="lg:col-span-2">
            <CardHeader title={t('timeline.title')} />
            <Timeline items={s.timeline} />
          </Card>

          <Card padded={false} className="lg:col-span-3">
            <CardHeader title={t('attendance.notWorkingStretches')} subtitle={t('attendance.notWorkingStretchesNote')} className="p-5 pb-0" />
            {s.idleSessions.length === 0 ? (
              <EmptyState title={t('attendance.noStretches')} hint={s.agentDevice ? t('attendance.noStretchesHint') : t('attendance.notTracked')} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('attendance.stretch')}</Th>
                    <Th>{t('attendance.reason')}</Th>
                    <Th>{t('attendance.lastActivity')}</Th>
                    <Th align="end">{t('attendance.penaltyCol')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.idleSessions.map((i) => {
                    const minutes = Math.floor(
                      ((i.endedAt ? new Date(i.endedAt) : new Date()).getTime() - new Date(i.startedAt).getTime()) / 60_000,
                    );
                    return (
                      <Tr key={i.id}>
                        <Td className="tabular">
                          <TimeRange from={i.startedAt} to={i.endedAt} />
                          <span className="block text-caption text-muted">
                            {i.endedAt ? formatMinutes(minutes, locale) : t('attendance.stillNotWorking')}
                            {i.endReason && i.endReason !== 'ACTIVITY_RESUMED' && ` · ${t(`attendance.endReason.${i.endReason}` as TranslationKey)}`}
                          </span>
                        </Td>
                        <Td>{t(`presence.reason.${i.reason}` as TranslationKey)}</Td>
                        <Td className="tabular text-muted">{formatShortTime(i.lastActivityAt, locale)}</Td>
                        <Td align="end">
                          <StretchPenalty stretch={i} />
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

/** What a stretch's penalty came to. */
function StretchPenalty({ stretch }: { stretch: IdleStretch }) {
  const { t, locale } = useI18n();
  if (stretch.penalty) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        <span className="tabular text-error-fg dark:text-error-dark">{t('attendance.penalty', { days: formatDays(stretch.penalty.days, locale) })}</span>
        <Badge tone={statusTone(stretch.penalty.status)}>{enumLabel(stretch.penalty.status, locale)}</Badge>
      </span>
    );
  }
  if (!stretch.penaltyOutcome) {
    return stretch.penaltyDueAt ? (
      <span className="text-caption text-muted">{t('attendance.penaltyDueAt', { time: formatShortTime(stretch.penaltyDueAt, locale) })}</span>
    ) : (
      <span className="text-faint">—</span>
    );
  }
  return (
    <span className={clsx('text-caption', stretch.penaltyOutcome === 'CAP_REACHED' ? 'text-warning-fg dark:text-warning-dark' : 'text-muted')}>
      {t(`attendance.outcome.${stretch.penaltyOutcome}` as TranslationKey)}
    </span>
  );
}

/** The shift's lines in time order, each with what it means in words. */
function Timeline({ items }: { items: TimelineItem[] }) {
  const { t, locale, localized } = useI18n();
  if (items.length === 0) return <p className="text-body-sm text-muted">{t('timeline.empty')}</p>;

  const describe = (item: TimelineItem): { icon: ReactNode; text: string; detail?: ReactNode; tone?: 'error' | 'success' | 'warning' } => {
    const m = item.metadata ?? {};
    switch (item.kind) {
      case 'SHIFT_STARTED':
        return { icon: <LogIn className="h-4 w-4 flip-rtl" />, text: t('timeline.SHIFT_STARTED') };
      case 'SHIFT_ENDED':
        return {
          icon: <LogOut className="h-4 w-4 flip-rtl" />,
          text: t('timeline.SHIFT_ENDED'),
          detail: m.autoClosed ? t('attendance.autoClosed') : m.closedBy ? t('attendance.closedBy', { name: localized(m.closedBy) }) : null,
        };
      case 'BREAK_STARTED':
        return { icon: <Coffee className="h-4 w-4" />, text: t('timeline.BREAK_STARTED', { type: localized(m.breakType) }) };
      case 'BREAK_ENDED':
        return { icon: <Play className="h-4 w-4 flip-rtl" />, text: t('timeline.BREAK_ENDED', { type: localized(m.breakType) }) };
      // Pause work (2026-10-24): no break, and no type to name.
      case 'PAUSE_STARTED':
        return { icon: <Pause className="h-4 w-4" />, text: t('timeline.PAUSE_STARTED') };
      case 'PAUSE_ENDED':
        return { icon: <Play className="h-4 w-4 flip-rtl" />, text: t('timeline.PAUSE_ENDED') };
      case 'AGENT_CONNECTED':
        return { icon: <MonitorCheck className="h-4 w-4" />, text: t('timeline.AGENT_CONNECTED', { device: m.device ?? '' }) };
      case 'AGENT_DISCONNECTED':
        return {
          icon: <MonitorOff className="h-4 w-4" />,
          text: t(`timeline.AGENT_DISCONNECTED.${['QUIT', 'LOST', 'SHUTDOWN', 'LOGOFF', 'UNPAIR', 'DISABLED', 'REMOVED'].includes(m.reason) ? m.reason : 'QUIT'}` as TranslationKey),
          tone: 'warning',
        };
      case 'AGENT_RECONNECTED':
        return { icon: <MonitorCheck className="h-4 w-4" />, text: t('timeline.AGENT_RECONNECTED') };
      case 'AGENT_SWITCHED':
        return { icon: <MonitorSmartphone className="h-4 w-4" />, text: t('timeline.AGENT_SWITCHED', { to: m.to ?? '' }) };
      case 'PRESENCE_WORKING':
        return {
          icon: <UserCheck className="h-4 w-4" />,
          text: m.reason === 'ACTIVITY_RESUMED' ? t('timeline.workingAgain') : t('presence.WORKING'),
          tone: 'success',
        };
      case 'PRESENCE_NOT_WORKING':
        return {
          icon: <UserX className="h-4 w-4" />,
          text: presenceLabel({ status: 'NOT_WORKING', reason: m.reason ?? null }, t) ?? t('presence.NOT_WORKING'),
          detail: m.lastActivityAt ? t('timeline.lastActivity', { time: formatShortTime(m.lastActivityAt, locale) }) : null,
          tone: 'error',
        };
      case 'DEVICE_LOCKED':
        return { icon: <Lock className="h-4 w-4" />, text: t('timeline.DEVICE_LOCKED') };
      case 'DEVICE_UNLOCKED':
        return { icon: <Unlock className="h-4 w-4" />, text: t('timeline.DEVICE_UNLOCKED') };
      case 'DEVICE_SLEEP':
        return { icon: <Moon className="h-4 w-4" />, text: t('timeline.DEVICE_SLEEP'), tone: 'warning' };
      case 'DEVICE_RESUMED':
        return { icon: <Sun className="h-4 w-4" />, text: t('timeline.DEVICE_RESUMED') };
      case 'IDLE_PENALTY_APPLIED':
        return {
          icon: <AlertTriangle className="h-4 w-4" />,
          text: t(m.pending ? 'timeline.IDLE_PENALTY_PENDING' : 'timeline.IDLE_PENALTY_APPLIED', { days: formatDays(m.days, locale) }),
          detail: m.configuredDays && Number(m.configuredDays) !== Number(m.days) ? t('timeline.cutToMaximum') : null,
          tone: 'error',
        };
      case 'IDLE_PENALTY_CAP_REACHED':
        return { icon: <AlertTriangle className="h-4 w-4" />, text: t('timeline.IDLE_PENALTY_CAP_REACHED'), tone: 'warning' };
      // A break that needs an administrator's approval: asked for, then decided or withdrawn.
      case 'BREAK_REQUESTED':
        return { icon: <Hourglass className="h-4 w-4" />, text: t('timeline.BREAK_REQUESTED', { type: localized(m.breakType) }) };
      case 'BREAK_REQUEST_APPROVED':
        return {
          icon: <ShieldCheck className="h-4 w-4" />,
          text: t('timeline.BREAK_REQUEST_APPROVED', { type: localized(m.breakType), name: localized(m.by) }),
          tone: 'success',
        };
      case 'BREAK_REQUEST_REJECTED':
        return {
          icon: <ShieldX className="h-4 w-4" />,
          text: t('timeline.BREAK_REQUEST_REJECTED', { type: localized(m.breakType), name: localized(m.by) }),
          // The approver's own words, in their own direction.
          detail: m.note ? <span dir="auto">“{m.note}”</span> : null,
          tone: 'warning',
        };
      case 'BREAK_REQUEST_CANCELLED':
        return {
          icon: <Undo2 className="h-4 w-4 flip-rtl" />,
          text: t('timeline.BREAK_REQUEST_CANCELLED', { type: localized(m.breakType) }),
          detail: m.reason ? t(`breakRequest.cancel.${m.reason}` as TranslationKey) : null,
        };
      default:
        return { icon: <Clock className="h-4 w-4" />, text: enumLabel(item.kind, locale) };
    }
  };

  return (
    <ol className="relative space-y-3 border-s hairline ps-5">
      {items.map((item, index) => {
        const line = describe(item);
        return (
          <li key={`${item.kind}-${item.at}-${index}`} className="relative">
            <span
              className={clsx(
                'absolute -start-[1.85rem] top-0 grid h-6 w-6 place-items-center rounded-full surface',
                line.tone === 'error' ? 'text-error-fg dark:text-error-dark' : line.tone === 'success' ? 'text-success-fg dark:text-success' : line.tone === 'warning' ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-tertiary)]',
              )}
              aria-hidden
            >
              {line.icon}
            </span>
            <p className="text-body-sm">
              {/* Isolated, never forced left to right: "8:05 م" keeps its own order in Arabic. */}
              <bdi className="me-2 tabular text-muted">{formatShortTime(item.at, locale)}</bdi>
              <span className={clsx(line.tone === 'error' ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]')}>{line.text}</span>
            </p>
            {line.detail && <p className="text-caption text-muted">{line.detail}</p>}
          </li>
        );
      })}
    </ol>
  );
}

/** The link to a shift's page, for a list. */
export function ShiftLink({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Link to={`/attendance/sessions/${id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
      {children}
    </Link>
  );
}
